
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import { addDoc, collection, query, where, getDocs, setDoc, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { createUserWithEmailAndPassword, sendEmailVerification, onAuthStateChanged } from "firebase/auth";
import Checkbox from './Checkbox';
import Toast from './Toast';
import { useSearchParams } from "react-router-dom";

const RegistrationForm = () => {
  const [searchParams] = useSearchParams();
  const initialStep = searchParams.get("step") || 0;
  const [step, setStep] = useState(parseInt(initialStep));
  const [teamSize, setTeamSize] = useState(1);
  const [rulesAgreed, setRulesAgreed] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const navigate = useNavigate();

  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [sendButtonTimer, setSendButtonTimer] = useState(0);
  const [showSubmissionPopup, setShowSubmissionPopup] = useState(false); // New state
  const [docId, setDocId] = useState(null); // Store document ID if it exists

  const initialTeamMember = { salutation: '', name: '', bloodGroup: '', birthdate: '', contactNumber: '' };

  const [formData, setFormData] = useState({
    teamName: '',
    teamLeaderSalutation: '',
    teamLeaderName: '',
    teamLeaderBloodGroup: '',
    teamLeaderBirthdate: '',
    email: '',
    contactNumber: '',
    theme: '',
    type: '',
    teamMembers: [],
    instituteName: '',
    projectTitle: '',
    projectDescription: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (step === 1) {
      setFormData(prevFormData => ({
        ...prevFormData,
        teamMembers: Array(teamSize - 1).fill(null).map((_, index) => (
          prevFormData.teamMembers[index] ? { ...prevFormData.teamMembers[index] } : { ...initialTeamMember }
        ))
      }));
    }
  }, [teamSize, step]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setIsVerified(user.emailVerified);
        if (initialStep === "0" && user.emailVerified) {
          navigate(`/register?step=1`);
          setStep(1);
        }
        fetchExistingData(user.email);

      } else {
        setIsVerified(false);
      }
    });

    return () => unsubscribe();
  }, [initialStep, navigate]);

  useEffect(() => {
    const checkEmailVerification = () => {
      if (auth.currentUser) {
        auth.currentUser.reload().then(() => {
          setIsEmailVerified(auth.currentUser.emailVerified);
        });
      }
    };
    const interval = setInterval(checkEmailVerification, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const storedVerification = localStorage.getItem("isVerified");
    if (storedVerification === "true") {
      setIsEmailVerified(true);
    }
  }, []);

  useEffect(() => {
    let intervalId;

    if (sendButtonTimer > 0) {
      intervalId = setInterval(() => {
        setSendButtonTimer(prevTimer => prevTimer - 1);
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [sendButtonTimer]);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleResendVerification = async () => {
    if (auth.currentUser) {
      try {
        await sendEmailVerification(auth.currentUser);
        setToastMessage("Verification email resent!");
        setToastType("success");
        setShowToast(true);
        setResendDisabled(true);
        setSendButtonTimer(30);

      } catch (error) {
        setSignupError(error.message);
        setToastMessage(`Error resending verification: ${error.message}`);
        setToastType("error");
        setShowToast(true);
      }
    }
  };

  const handleSendVerification = async () => {
    if (signupPassword !== confirmPassword) {
      setSignupError("Passwords do not match!");
      setToastMessage("Passwords do not match!");
      setToastType("error");
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    setSignupError("");

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, signupEmail, signupPassword);
      const user = userCredential.user;

      await sendEmailVerification(user);
      setVerificationSent(true);
      setToastMessage("Verification email sent! Please check your inbox.");
      setToastType("success");
      setShowToast(true);
      setSendButtonTimer(30);

    } catch (error) {
      setSignupError(error.message);
      setToastMessage(`Error sending verification: ${error.message}`);
      setToastType("error");
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSignupEmail("");
    setSignupPassword("");
    setConfirmPassword("");
    setSignupError("");

    const checkVerification = async () => {
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        const isVerified = user.emailVerified;
        localStorage.setItem("isVerified", isVerified);
        setIsEmailVerified(isVerified);
      }
    };

    checkVerification();
  }, []);

  const validateStep = (currentStep) => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!signupEmail.trim()) {
        newErrors.signupEmail = 'Email is required';
        setToastMessage('Email is required');
        setToastType("error");
        setShowToast(true);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail.trim())) {
        newErrors.signupEmail = 'Enter a valid email address';
        setToastMessage('Enter a valid email address');
        setToastType("error");
        setShowToast(true);
      }

      if (!signupPassword.trim()) {
        newErrors.signupPassword = 'Password is required';
        setToastMessage('Password is required');
        setToastType("error");
        setShowToast(true);
      } else if (signupPassword.trim().length < 6) {
        newErrors.signupPassword = 'Password must be at least 6 characters long';
        setToastMessage('Password must be at least 6 characters long');
        setToastType("error");
        setShowToast(true);
      }

      if (!confirmPassword.trim()) {
        newErrors.confirmPassword = 'Confirm Password is required';
        setToastMessage('Confirm Password is required');
        setToastType("error");
        setShowToast(true);
      } else if (signupPassword !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
        setToastMessage('Passwords do not match');
        setToastType("error");
        setShowToast(true);
      }

      if (!isEmailVerified) {
        newErrors.isEmailVerified = 'Email verification is required before proceeding';
        setToastMessage('Email verification is required before proceeding');
        setToastType("error");
        setShowToast(true);
      }
    }


    if (currentStep === 1) {
      if (!formData.teamName.trim()) {
        newErrors.teamName = 'Team name is required';
        setToastMessage('Team name is required');
        setToastType("error");
        setShowToast(true);
      } else if (formData.teamName.trim().length < 3) {
        newErrors.teamName = 'Team name must be at least 3 characters long';
        setToastMessage('Team name must be at least 3 characters long');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.teamLeaderSalutation) {
        newErrors.teamLeaderSalutation = 'Salutation is required';
        setToastMessage('Salutation is required');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.teamLeaderName.trim()) {
        newErrors.teamLeaderName = 'Team leader name is required';
        setToastMessage('Team leader name is required');
        setToastType("error");
        setShowToast(true);
      } else if (formData.teamLeaderName.trim().length < 3) {
        newErrors.teamLeaderName = 'Team leader name must be at least 3 characters long';
        setToastMessage('Team leader name must be at least 3 characters long');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.teamLeaderBloodGroup) {
        newErrors.teamLeaderBloodGroup = 'Team leader blood group is required';
        setToastMessage('Team leader blood group is required');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.teamLeaderBirthdate) {
        newErrors.teamLeaderBirthdate = 'Birthdate is required';
        setToastMessage('Birthdate is required');
        setToastType("error");
        setShowToast(true);
      } else if (new Date(formData.teamLeaderBirthdate) > new Date()) {
        newErrors.teamLeaderBirthdate = 'Birthdate cannot be in the future';
        setToastMessage('Birthdate cannot be in the future');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.email) {
        newErrors.email = 'Email is required';
        setToastMessage('Email is required');
        setToastType("error");
        setShowToast(true);
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        setToastMessage('Please enter a valid email address');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.contactNumber) {
        newErrors.contactNumber = 'Contact number is required';
        setToastMessage('Contact number is required');
        setToastType("error");
        setShowToast(true);
      } else if (!validatePhone(formData.contactNumber)) {
        newErrors.contactNumber = 'Please enter a valid 10-digit mobile number';
        setToastMessage('Please enter a valid 10-digit mobile number');
        setToastType("error");
        setShowToast(true);
      }

      for (let i = 0; i < teamSize - 1; i++) {
        const member = formData.teamMembers[i];

        if (!member) continue;

        if (!member.salutation) {
          newErrors[`teamMemberSalutation${i}`] = `Team member ${i + 2} salutation is required`;
          setToastMessage(`Team member ${i + 2} salutation is required`);
          setToastType("error");
          setShowToast(true);
        }
        if (!member.name?.trim()) {
          newErrors[`teamMemberName${i}`] = `Team member ${i + 2} name is required`;
          setToastMessage(`Team member ${i + 2} name is required`);
          setToastType("error");
          setShowToast(true);
        } else if (member.name.trim().length < 3) {
          newErrors[`teamMemberName${i}`] = `Team member ${i + 2} name must be at least 3 characters long`;
          setToastMessage(`Team member ${i + 2} name must be at least 3 characters long`);
          setToastType("error");
          setShowToast(true);
        }
        if (!member.bloodGroup) {
          newErrors[`teamMemberBloodGroup${i}`] = `Team member ${i + 2} blood group is required`;
          setToastMessage(`Team member ${i + 2} blood group is required`);
          setToastType("error");
          setShowToast(true);
        }
        if (!member.birthdate) {
          newErrors[`teamMemberBirthdate${i}`] = `Team member ${i + 2} birthdate is required`;
          setToastMessage(`Team member ${i + 2} birthdate is required`);
          setToastType("error");
          setShowToast(true);
        } else if (new Date(member.birthdate) > new Date()) {
          newErrors[`teamMemberBirthdate${i}`] = `Team member ${i + 2} birthdate cannot be in the future`;
          setToastMessage(`Team member ${i + 2} birthdate cannot be in the future`);
          setToastType("error");
          setShowToast(true);
        }
        if (!member.contactNumber) {
          newErrors[`teamMemberContactNumber${i}`] = `Team member ${i + 2} contact number is required`;
          setToastMessage(`Team member ${i + 2} contact number is required`);
          setToastType("error");
          setShowToast(true);
        } else if (!validatePhone(member.contactNumber)) {
          newErrors[`teamMemberContactNumber${i}`] = `Team member ${i + 2} contact number is invalid`;
          setToastMessage(`Team member ${i + 2} contact number is invalid`);
          setToastType("error");
          setShowToast(true);
        }
      }

    }

    if (currentStep === 2) {
      if (!formData.theme) {
        newErrors.theme = 'Theme selection is required';
        setToastMessage('Theme selection is required');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.type) {
        newErrors.type = 'Type selection is required';
        setToastMessage('Type selection is required');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.instituteName.trim()) {
        newErrors.instituteName = 'Institute name is required';
        setToastMessage('Institute name is required');
        setToastType("error");
        setShowToast(true);
      } else if (formData.instituteName.trim().length < 3) {
        newErrors.instituteName = 'Institute name must be at least 3 characters long';
        setToastMessage('Institute name must be at least 3 characters long');
        setToastType("error");
        setShowToast(true);
      }
      if (!formData.projectTitle.trim()) {
        newErrors.projectTitle = 'Project title is required';
        setToastMessage('Project title is required');
        setToastType("error");
        setShowToast(true);
      } else if (formData.projectTitle.trim().length < 5) {
        newErrors.projectTitle = 'Project title must be at least 3 characters long';
        setToastMessage('Project title must be at least 3 characters long');
        setToastType("error");
        setShowToast(true);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleTeamSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setTeamSize(newSize);

  };

  const handleTeamMemberChange = (index, field, value) => {
    const newTeamMembers = [...formData.teamMembers];
    const sanitizedValue = field === 'contactNumber' && typeof value === 'number' ? String(value) : value;
    newTeamMembers[index] = { ...newTeamMembers[index], [field]: sanitizedValue };
    setFormData(prev => ({
      ...prev,
      teamMembers: newTeamMembers
    }));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`teamMemberSalutation${index}`];
      delete newErrors[`teamMemberName${index}`];
      delete newErrors[`teamMemberBloodGroup${index}`];
      delete newErrors[`teamMemberBirthdate${index}`];
      delete newErrors[`teamMemberContactNumber${index}`];
      return newErrors;
    });
  };

  const handleNextStep = async () => {
    if (step === 0) {
      if (!auth.currentUser) {
        setToastMessage("No user found. Please log in again.");
        setToastType("error");
        setShowToast(true);
        return;
      }

      setIsVerifyingEmail(true);

      try {
        await auth.currentUser.reload();
        if (!auth.currentUser.emailVerified) {
          setShowSubmissionPopup(true);
          setToastMessage("Please verify your email before proceeding.");
          setToastType("error");
          setShowToast(true);
          return;
        } else {
          setIsVerified(true);
          setToastMessage("Email verified! Proceeding to the next step.");
          setToastType("success");
          setShowToast(true);
          setStep(step + 1);
        }
      } catch (error) {
        console.error("Error reloading user:", error);
        setToastMessage("Failed to verify email. Please try again.");
        setToastType("error");
        setShowToast(true);
      } finally {
        setIsVerifyingEmail(false);
      }
    }
    else {
      if (validateStep(step)) {
        setStep(step + 1);
      } else {
        validateStep(step);
      }
    }
  };


  const handlePrevStep = () => {
    setStep(step - 1);
  };

  // Function to fetch existing data from Firestore
  const fetchExistingData = async (email) => {
    try {
      const teamsRef = collection(db, 'teams');
      const q = query(teamsRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // If data exists, populate the form.
        const doc = querySnapshot.docs[0].data();
        setDocId(querySnapshot.docs[0].id); // Save document ID

        // Ensure teamMembers are handled correctly based on teamSize:
        const teamMembers = doc.teamMembers || [];
        const teamSizeToUse = Math.max(teamMembers.length + 1, 1);

        setFormData(prevFormData => ({
          ...prevFormData,
          ...doc,
          teamMembers: teamMembers.map(member => ({ ...member })),

        }));
        setTeamSize(teamSizeToUse);
        console.log("Data fetched and form populated:", doc);
      } else {
        console.log("No existing data found for this email.");
        setDocId(null)
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setToastMessage("Failed to retrieve existing data.");
      setToastType("error");
      setShowToast(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isVerified) {
      setToastMessage("Please verify your email before submitting.");
      setToastType("error");
      setShowToast(true);
      return;
    }
    if (!rulesAgreed) {
      setToastMessage("Please agree to the rules and regulations before submitting.");
      setToastType("error");
      setShowToast(true);
      return;
    }
    if (!validateStep(2)) {
      return; // Prevent submission if there are validation errors
    }

    setIsSubmitting(true);
    setSubmissionSuccess(false);
    setProgress(0);

    const intervalId = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 10;
        return newProgress > 95 ? 95 : newProgress;
      });
    }, 200);

    try {
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate server delay

      const dataToSave = {
        teamName: formData.teamName,
        teamLeaderSalutation: formData.teamLeaderSalutation,
        teamLeaderName: formData.teamLeaderName,
        teamLeaderBloodGroup: formData.teamLeaderBloodGroup,
        teamLeaderBirthdate: formData.teamLeaderBirthdate,
        email: formData.email,
        contactNumber: formData.contactNumber,
        theme: formData.theme,
        type: formData.type,
        teamMembers: formData.teamMembers.slice(0, teamSize - 1),
        instituteName: formData.instituteName,
        projectTitle: formData.projectTitle,
        projectDescription: formData.projectDescription,
        submittedAt: new Date()
      };

      if (docId) {
        // Update existing document
        await setDoc(doc(db, 'teams', docId), dataToSave);
      } else {
        // Create new document
        await addDoc(collection(db, 'teams'), dataToSave);
      }


      clearInterval(intervalId);
      setProgress(100);
      setSubmissionSuccess(true);

      setTimeout(() => {
        setIsSubmitting(false);
        setShowSubmissionPopup(true);
      }, 2000);
    } catch (error) {
      clearInterval(intervalId);
      setIsSubmitting(false);
      setToastMessage("There was an error submitting your registration. Please try again.");
      setToastType("error");
      setShowToast(true);
    }
  };


  const capitalizeWords = (str) => {
    if (typeof str !== 'string') {
      return str != null ? str.toString() : '';
    }
    if (!str) return '';
    return str.replace(/\b\w/g, char => char.toUpperCase());
  };

  const renderError = (fieldName) => {
    return errors[fieldName] && (
      <p className="text-red-500 text-sm mt-1">{errors[fieldName]}</p>
    );
  };

  const handleResendAgain = () => {
    setShowSubmissionPopup(false);
    handleResendVerification();
    setShowToast(false);
    return;
  }

  const handleGoHome = () => {
    setShowSubmissionPopup(false);
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 py-16 relative">

      {isSubmitting && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-96 text-center">
            {!submissionSuccess ? (
              <>
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">Saving Data...</h2>
                <div className="relative pt-1">
                  <div className="overflow-hidden h-2 mb-4 flex rounded bg-blue-200">
                    <div
                      style={{ width: `${progress}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                    />
                  </div>
                </div>
                <p className="text-gray-600">Please wait while we save your information securely.</p>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-extrabold text-green-600 mb-4 animate-pulse">Submitted Successfully!</h2>
                <CheckCircle className="mx-auto w-16 h-16 text-green-500 animate-bounce mb-4" />
                <p className="text-gray-600">Your team is now registered.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Verification Popup */}
      {showSubmissionPopup && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-96 text-center">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Thank you for registering!</h2>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleGoHome}
                className="px-6 py-3 rounded-lg text-gray-600 text-sm font-medium bg-gray-200 hover:bg-gray-300 focus:outline-none"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      )}

      {isVerifyingEmail && (


        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex items-center justify-center">
            <svg className="animate-spin h-6 w-6 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="ml-2">Verifying Email...</span>
          </div>
        </div>


      )}

      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Register Your Team
          </h1>
          <p className="text-lg text-gray-600">
            Fill in the details below to participate in the Science Exhibition
          </p>
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center">
            {['Sign Up', 'Team Details', 'Project Info', 'Review'].map((label, index) => (
              <div key={label} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 
              ${step > index ? 'bg-green-500 text-white' :
                    step === index ? 'bg-blue-600 text-white' :
                      'bg-gray-200 text-gray-400'}`}>
                  {step > index ? <CheckCircle className="w-6 h-6" /> : index}
                </div>
                <span className={`text-sm ${step === index ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
          <div className="relative mt-4 h-2 bg-gray-200 rounded-full">
            <div
              className="absolute top-0 left-0 h-full bg-blue-600 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8">
          {step === 0 && (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900">Sign Up</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email*
                </label>
                <input
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password*
                </label>
                <input
                  type="password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password*
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {signupError && (
                <p className="text-red-500 text-sm mt-1">{signupError}</p>
              )}

              <div className="flex flex-col space-y-3">
                {!verificationSent ? (
                  <button
                    type="button"
                    onClick={handleSendVerification}
                    disabled={isLoading || sendButtonTimer > 0}
                    className={`px-6 py-3 rounded-lg text-white text-sm font-medium ${isLoading || sendButtonTimer > 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"}`}
                  >
                    {isLoading ? "Processing..." : "Send Verification Email"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendDisabled || sendButtonTimer > 0}
                    className={`px-6 py-3 rounded-lg text-white text-sm font-medium ${resendDisabled || sendButtonTimer > 0
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"}`}
                  >
                    {resendDisabled || sendButtonTimer > 0
                      ? `Resend in ${sendButtonTimer}s`
                      : "Resend Verification Email"}
                  </button>
                )}

              </div>

              {!isEmailVerified && (
                <p className="text-sm text-gray-600 mt-2">
                  Please verify your email before proceeding.
                </p>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Name*
                </label>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleInputChange}
                  placeholder="Enter your team name"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.teamName ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('teamName')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size*
                </label>
                <select
                  value={teamSize}
                  onChange={handleTeamSizeChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-50"
                >
                  <option value={1}>1 Member</option>
                  <option value={2}>2 Members</option>
                  <option value={3}>3 Members</option>
                  <option value={4}>4 Members</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader Salutation*
                </label>
                <select
                  name="teamLeaderSalutation"
                  value={formData.teamLeaderSalutation}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.teamLeaderSalutation ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select Salutation</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                </select>
                {renderError('teamLeaderSalutation')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader Name*
                </label>
                <input
                  type="text"
                  name="teamLeaderName"
                  value={formData.teamLeaderName}
                  onChange={handleInputChange}
                  placeholder="Enter team leader's full name"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.teamLeaderName ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('teamLeaderName')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader's Blood Group*
                </label>
                <select
                  name="teamLeaderBloodGroup"
                  value={formData.teamLeaderBloodGroup}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.teamLeaderBloodGroup ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
                {renderError('teamLeaderBloodGroup')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader's Birthdate*
                </label>
                <input
                  type="date"
                  name="teamLeaderBirthdate"
                  value={formData.teamLeaderBirthdate}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.teamLeaderBirthdate ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('teamLeaderBirthdate')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader's Email*
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter a valid email address"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('email')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Leader's Contact Number*
                </label>
                <input
                  type="tel"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.contactNumber ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('contactNumber')}
              </div>

              {/* Team Members Details */}
              <div className="space-y-4">
                {formData.teamMembers.map((member, index) => (
                  <div key={index} className="space-y-1">
                    <h4 className="text-lg font-semibold text-gray-700">Team Member {index + 2} Details</h4>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Salutation*
                    </label>
                    <select
                      name={`teamMemberSalutation${index}`}
                      value={member?.salutation || ''}
                      onChange={(e) => handleTeamMemberChange(index, 'salutation', e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="">Select Salutation</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                    </select>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name*
                    </label>
                    <input
                      type="text"
                      value={member?.name || ''}
                      onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                      placeholder={`Enter Team Member ${index + 2} Name`}
                      className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Blood Group*
                    </label>
                    <select
                      name={`teamMemberBloodGroup${index}`}
                      value={member?.bloodGroup || ''}
                      onChange={(e) => handleTeamMemberChange(index, 'bloodGroup', e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    >
                      <option value="">Select Blood Group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </select>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Birthdate*
                    </label>
                    <input
                      type="date"
                      value={member?.birthdate || ''}
                      onChange={(e) => handleTeamMemberChange(index, 'birthdate', e.target.value)}
                      className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Number*
                    </label>
                    <input
                      type="tel"
                      name={`teamMemberContactNumber${index}`}
                      value={member?.contactNumber || ''}
                      onChange={(e) => handleTeamMemberChange(index, 'contactNumber', e.target.value)}
                      placeholder="Enter 10-digit mobile number"
                      className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme*
                </label>
                <select
                  name="theme"
                  value={formData.theme}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.theme ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="">Select Theme</option>
                  <option value="Social Impact">Social Impact</option>
                  <option value="Economic Impact">Economic Impact</option>
                  <option value="Visual Physics Software">Visual Physics Software</option>
                </select>
                {renderError('theme')}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type*
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.type ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >                  <option value="">Select Type</option>
                  <option value="Software">Software</option>
                  <option value="Hardware">Hardware</option>
                </select>
                {renderError('type')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute Name*
                </label>
                <input
                  type="text"
                  name="instituteName"
                  value={formData.instituteName}
                  onChange={handleInputChange}
                  placeholder="Enter your institute name"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.instituteName ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('instituteName')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Title*
                </label>
                <input
                  type="text"
                  name="projectTitle"
                  value={formData.projectTitle}
                  onChange={handleInputChange}
                  placeholder="Enter your project title"
                  className={`w-full px-4 py-3 rounded-lg border ${errors.projectTitle ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                />
                {renderError('projectTitle')}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Description*
                </label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  placeholder="Describe your project (minimum 50 characters)"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Review Your Information</h3>
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                {Object.entries(formData).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">
                      {capitalizeWords(key.replace(/([A-Z])/g, ' $1').trim())}:
                    </dt>
                    <dd className="text-sm text-gray-900 col-span-2 break-words">
                      {capitalizeWords(value)}
                    </dd>
                  </div>
                ))}
              </div>
              {/* Rules and Regulations */}
              <div className="mt-6 space-y-4">
                <h4 className="text-lg font-semibold text-blue-600">Rules and Regulations</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="max-h-80 overflow-y-auto pr-4">
                    <div className="space-y-6">
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Registration and Participation</h5>
                        <ol className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Participants must register in advance and submit their project titles with a brief description by the given deadline.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Each team should consist of 1–4 members. Individual participation may also be allowed depending on the rules.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Participants must present their institutional ID cards on the day of the exhibition.</li>
                        </ol>
                      </div>
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Project Guidelines</h5>
                        <ol start="4" className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Projects must be original and should not have been presented in any prior competitions.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">The project should be relevant to the theme (if any) of the exhibition.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Participants are responsible for arranging their materials, tools, and equipment. Only basic facilities like tables, electricity, or water will be provided by the organizers.</li>
                        </ol>
                      </div>
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Setup and Display</h5>
                        <ol start="7" className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Teams must set up their projects at the designated location within the allotted time. Latecomers may be disqualified.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Projects must display the title, team member names, and college name clearly.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">All exhibits must be secured to prevent accidents or damage during the exhibition.</li>
                        </ol>
                      </div>
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Safety and Conduct</h5>
                        <ol start="10" className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Projects must not pose any safety risks to participants, judges, or visitors (e.g., hazardous chemicals, sharp objects, open flames).</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Teams should be present at their stations throughout the event to explain and demonstrate their projects.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Respectful behavior and professional conduct toward judges, volunteers, and other participants are mandatory.</li>
                        </ol>
                      </div>
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Evaluation</h5>
                        <ol start="13" className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Projects will be judged based on creativity, scientific methodology, presentation, and relevance to the theme.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">The decision of the judges will be final and binding.</li>
                        </ol>
                      </div>
                      <div>
                        <h5 className="text-md font-semibold text-gray-900 mb-2">Other Important Rules</h5>
                        <ol start="15" className="list-decimal list-inside space-y-2 md:pl-4">
                          <li className="text-sm text-gray-700 leading-relaxed">Teams must follow the event schedule, including setup and dismantling times.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Use of pre-made or plagiarized content will result in immediate disqualification.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Snacks or refreshments, if provided, should be consumed only in designated areas.</li>
                          <li className="text-sm text-gray-700 leading-relaxed">Emergency procedures, if required, must be adhered to promptly.</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 bg-blue-50 p-4 rounded-lg">
                  <Checkbox
                    id="rules"
                    checked={rulesAgreed}
                    onCheckedChange={(checked) => setRulesAgreed(checked)}
                  >
                    <span className="text-sm text-blue-800 font-medium">
                      I have read and agree to the rules and regulations
                    </span>
                  </Checkbox>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            {step > 0 && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ChevronLeft className="mr-2 h-5 w-5" />
                Previous
              </button>
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={handleNextStep}
                disabled={step === 0 && !isEmailVerified}
                className={`ml-auto flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${step === 0 && !isEmailVerified ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Next
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            ): (
              <button
                type="submit"
                disabled={!rulesAgreed}
                className={`ml-auto flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${rulesAgreed ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
              >
                Submit
                <CheckCircle className="ml-2 h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-center text-sm text-gray-600 mt-4">
            Already have an account?
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:underline ml-1"
            >
              Log in
            </button>
          </p>
        </form>
      </div>
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

// const RegistrationForm = () => {
//   return (
//     <div>
//     <h2>registration Closed</h2>
//   </div>
//   );
// };
export default RegistrationForm;